import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/query-client';
import type { SessionUser } from '@/types/api';

// Último user confirmado por /api/auth/me en este browser. Metadata de UI
// (nombre/email/rol) para el arranque optimista — la credencial real sigue
// siendo la cookie de sesión, cada request se autoriza server-side.
const LAST_USER_KEY = 'redeapp.auth.lastUser';

// Valida la forma antes de confiar en el cache: un JSON válido pero con otro
// schema (ej. deploy que cambió SessionUser) entraría tipado como SessionUser
// y crashearía el shell en render — crash-loop hasta limpiar localStorage.
function isSessionUser(v: unknown): v is SessionUser {
  if (v === null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.email === 'string' &&
    typeof o.nombre === 'string' &&
    (o.rol === 'viewer' || o.rol === 'gestor' || o.rol === 'admin')
  );
}

function readCachedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(LAST_USER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isSessionUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: SessionUser | null): void {
  try {
    if (user) localStorage.setItem(LAST_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // storage bloqueado/lleno: solo se pierde el arranque optimista
  }
}

// Query de sesión ['auth','me'] — RequireAuth y el footer del Sidebar
// (UserMenu) consumen el mismo cache, no hay round-trips duplicados.
export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const data = await api<{ user: SessionUser }>('/api/auth/me');
        writeCachedUser(data.user);
        return data.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          writeCachedUser(null);
          return null;
        }
        throw err;
      }
    },
    // Arranque optimista: el último user confirmado en este browser entra
    // como placeholder mientras /me resuelve en paralelo.
    // ⚠ Sin cache devolver undefined, NUNCA null: para TanStack Query null ES
    // data válida (status 'success') → RequireAuth saltearía el loading y
    // rebotaría a /login con la sesión todavía verificándose.
    placeholderData: () => readCachedUser() ?? undefined,
    staleTime: 60_000,
  });
}
